'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@kit/ui/input-otp';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import {
  confirmSignupEmailVerificationAction,
  requestSignupEmailVerificationAction,
} from '../server/verify-signup-email-actions';

/**
 * Skippable email verification offered right after phone signup stored an
 * optional email. Auto-sends a session-bound 6-digit code to the address on
 * mount; the user can verify now or skip — either way `onDone` continues the
 * normal post-signup navigation. Skipping is safe: the unverified email is
 * kept but suppressed from sends, and the profile page + nudge banner offer
 * verification later.
 */
export function SignupEmailVerifyStep({
  email,
  onDone,
}: {
  email: string;
  onDone: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [state, setState] = React.useState({
    code: '',
    verifying: false,
    resendCooldown: 0,
  });
  // One-shot guard for the on-mount auto-send (Strict Mode double-invokes
  // effects; a ref keeps it to a single email).
  const sentRef = React.useRef(false);

  const sendCode = React.useCallback(async () => {
    const result = await requestSignupEmailVerificationAction({ email });

    if (!result.success) {
      if (result.code === 'email_taken') {
        toast.error(
          t(
            'auth:verifyEmailTaken',
            'This email now belongs to another account.',
          ),
        );
      } else {
        toast.error(
          t('auth:verifyEmailSendFailed', "We couldn't send the code. Try again."),
        );
      }
      return false;
    }

    return true;
  }, [email, t]);

  React.useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;

    setState((s) => ({ ...s, resendCooldown: 30 }));
    void sendCode();
  }, [sendCode]);

  React.useEffect(() => {
    if (state.resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setState((s) => ({ ...s, resendCooldown: s.resendCooldown - 1 }));
    }, 1000);

    return () => clearInterval(timer);
  }, [state.resendCooldown]);

  const handleVerify = async () => {
    setState((s) => ({ ...s, verifying: true }));

    try {
      const result = await confirmSignupEmailVerificationAction({
        code: state.code,
      });

      if (result.success) {
        toast.success(
          t('auth:verifyEmailSuccess', 'Email verified — you’re all set.'),
        );
        await onDone();
        return;
      }

      if (result.code === 'invalid_code') {
        toast.error(
          t('auth:verifyEmailInvalidCode', "That code didn't work. Try again."),
        );
        setState((s) => ({ ...s, code: '', verifying: false }));
        return;
      }

      toast.error(t('auth:errors.generic'));
      setState((s) => ({ ...s, verifying: false }));
    } catch {
      toast.error(t('auth:errors.generic'));
      setState((s) => ({ ...s, verifying: false }));
    }
  };

  const handleResend = async () => {
    setState((s) => ({ ...s, resendCooldown: 30 }));
    await sendCode();
  };

  return (
    <div className="flex w-full flex-col items-center gap-y-6">
      <div className="text-center">
        <p className="text-base font-medium">
          <Trans i18nKey="auth:verifyEmailStepTitle" defaults="Verify your email" />
        </p>

        <p className="text-muted-foreground mt-1 text-sm">
          <Trans
            i18nKey="auth:verifyEmailStepSubtitle"
            values={{ email }}
            defaults="We sent a 6-digit code to {{email}}. Enter it below, or skip and verify later from your profile."
          />
        </p>
      </div>

      <InputOTP
        maxLength={6}
        value={state.code}
        onChange={(code) => setState((s) => ({ ...s, code }))}
        disabled={state.verifying}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
        </InputOTPGroup>
        <InputOTPSeparator />
        <InputOTPGroup>
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>

      <div className="flex w-full flex-col gap-y-2">
        <Button
          type="button"
          disabled={state.code.length !== 6 || state.verifying}
          onClick={handleVerify}
          data-test="signup-email-verify-button"
        >
          {state.verifying ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              <Trans i18nKey="auth:verifyingCode" />
            </>
          ) : (
            <Trans i18nKey="auth:verifyEmailButton" defaults="Verify email" />
          )}
        </Button>

        <Button
          type="button"
          variant="ghost"
          disabled={state.verifying || state.resendCooldown > 0}
          onClick={handleResend}
        >
          {state.resendCooldown > 0 ? (
            <Trans
              i18nKey="auth:resendCodeIn"
              values={{ seconds: state.resendCooldown }}
            />
          ) : (
            <Trans i18nKey="auth:requestNewCode" />
          )}
        </Button>

        <Button
          type="button"
          variant="link"
          className="text-muted-foreground"
          disabled={state.verifying}
          onClick={() => void onDone()}
          data-test="signup-email-verify-skip-button"
        >
          <Trans i18nKey="auth:verifyEmailSkip" defaults="Skip for now" />
        </Button>
      </div>
    </div>
  );
}
